export enum VoucherOperationType {
    INWARD = 'CL_INWARD_TRANSFER',
    OUTWARD = 'CL_OUTWARD_TRANSFER',
    ALL = '',
}

export enum VOUCHER_STATUS {
    CANCELED = 'CANCELED',
    EXPIRED = 'EXPIRED',
    NEW = 'NEW',
    REDEEMED = 'REDEEMED',
}

export interface IVoucherInquiryRequest {
    voucher_code: string;
    operation_code: VoucherOperationType;
}

export interface IVoucherInquiryResponse {
    voucher_code: string;
    issued_on: string;
    expiry_time: string;
    voucher_status: VOUCHER_STATUS;
    amount: number;
}

export interface IVoucherListRequest {
    operation_code?: VoucherOperationType;
    agent_username?: string;
    agent_id?: string;
    status?: string;
    voucher_code?: string;
    limit: number;
    after: string;
}

export interface IVoucherListIndex {
    voucher_code: string;
    issued_on: string;
    expiry_time: string;
    voucher_status: VOUCHER_STATUS;
    amount: number;
    currency: string;
    operation_code: VoucherOperationType;
    user: {
        id: string;
        first_name: string;
        last_name: string;
        email: string;
    };
}

export interface IVoucherListIndexAugmented extends IVoucherListIndex {
    _name?: string;
}

export interface IVoucherListResponse {
    has_more: boolean;
    next_page?: string;
    data: IVoucherListIndex[];
}

export interface IVoucherGenerationRequest {
    currency: string;
    amount: number;
    account_id: string;
    note: string;
    operation_code: VoucherOperationType;
    agent_id?: string;
    agent_username?: string;
    agent_account_id?: string;
}
export interface IVoucherGenerationResponse {
    voucher_code: string;
    duration: number;
    issued_on: string;
    expiry_time: string;
}

export enum RedemptionChannel {
    FIB = 'FIBPGW',
}
export interface IVoucherRedemptionRequest {
    channel: RedemptionChannel;
    external_ref_number?: string;
    operation_code: VoucherOperationType;
    agent_account_id: string;
    amount: number;
}

export interface IVoucherRedemptionResponse {
    currency: string;
    amount: number;
    fee_amount: number;
    account_id: string;
    status: string;
    transaction_id: string;
}

// --- NEO Cash Out via Voucher Code (NEOIQ-2694 / NEOIQ-2700 / NEOIQ-2701) ---
// NEO's redeem call is a distinct, proxied endpoint confirmed against the BE-shared Postman
// collection "Nfront API - Proxy Cardless Redeem": POST nfront/v2/proxy/cardless/vouchers/{code}/redeems
// — note the extra `proxy` segment and the lack of a `channel` field, unlike FIB's
// IVoucherRedemptionRequest/RedemptionChannel above.

// Figma's "Cash Out via Voucher Code" screens (enter-voucher-code, voucher-details, cash-out-result)
// are all laid out against a fixed 627px-wide panel, not a percentage of viewport like
// SidePanelService's default 35%/90% sizing — on common laptop widths 35% renders narrower than
// 627px, which was wrapping the 9-digit voucher input onto two rows. Pass this to every
// `sidePanel.open(...)` call in this flow as the `minWidthPx` argument so the panel never renders
// narrower than Figma's design regardless of viewport width. Set a bit above the 627px frame itself
// (content needs ~578px for the 9 boxes once the panel's 24px side padding is subtracted) to leave
// headroom for a vertical scrollbar gutter rather than sizing exactly to the minimum.
export const NEO_CASH_OUT_PANEL_MIN_WIDTH_PX = 680;
export enum NeoCashOutOperationCode {
    CASH_OUT = 'CL_CASHOUT',
}

// Lookup endpoint confirmed against the BE-shared Postman collection:
// GET nfront/v2/proxy/cardless/vouchers/{code} — a plain GET by path param, no request body.
// Field names below are confirmed against a real sample response (not inferred) — note there is no
// `final_cash_amount` or phone number field; those were inferred guesses before and are now derived
// (see getNeoCashOutFinalCashAmount) / omitted below.
export interface INeoCashOutVoucherDetails {
    voucher_code: string;
    account_id: string;
    issued_on: string;
    expiry_time: string;
    voucher_status: VOUCHER_STATUS;
    reference_number: string;
    amount: number;
    currency: string;
    operation_code: NeoCashOutOperationCode;
    fee_amount: number;
    commission_amount: number;
    user: {
        id: string;
        first_name: string;
        last_name: string;
        email: string;
    };
}

// Figma's "Approve & Pay Cash" screen shows a "Cash to pay customer" amount distinct from the raw
// `amount` field, and a "Wallet will be credited" amount on top of that — neither is returned by
// the API, so both are derived client-side rather than duplicated across the three components that
// display them (voucher-details, approve-confirm-modal, cash-out-result).
//
// Confirmed with the user: "Service fees" shown to the agent is the combined fee_amount +
// commission_amount. "Cash to pay customer" is simply the transaction amount (the agent hands the
// customer the full voucher amount in cash), and "Wallet will be credited" (to the agent) is the
// transaction amount plus the commission_amount only (not the full combined service fee) — the
// agent is reimbursed the cash they paid out plus their commission.
export function getNeoCashOutServiceFee(details: Pick<INeoCashOutVoucherDetails, 'fee_amount' | 'commission_amount'>): number {
    return details.fee_amount + details.commission_amount;
}

export function getNeoCashOutFinalCashAmount(details: Pick<INeoCashOutVoucherDetails, 'amount'>): number {
    return details.amount;
}

export function getNeoCashOutWalletCredit(details: Pick<INeoCashOutVoucherDetails, 'amount' | 'commission_amount'>): number {
    return details.amount + details.commission_amount;
}

export interface INeoCashOutRedeemRequest {
    operation_code: NeoCashOutOperationCode;
    agent_account_id: string;
    amount: number;
    external_ref_number?: string;
}

// NEOIQ-2701: the redeem response is a much smaller, distinct shape from INeoCashOutVoucherDetails
// (the lookup response) — confirmed against a real sample. It has no voucher_code, user, reference
// number, or commission_amount, so the result screen is built by merging this onto the
// already-fetched lookup details rather than passing it through directly (see
// voucher-details.component.ts#redeem).
export interface INeoCashOutRedeemResponse {
    transaction_id: string;
    account_id: string;
    currency: string;
    amount: number;
    fee_amount: number;
    status: VOUCHER_STATUS;
}

// The lookup endpoint's error responses are RFC7807 "problem details" — `detail` is a free-text,
// human-readable sentence ("Voucher not found") not a stable code, so banners are matched on the
// `type` URL's last path segment instead (see enter-voucher-code.component.ts#handleError).
// NOT_FOUND is confirmed against a real 404 response: {"type":
// "https://developer.nymcard.com/v1/problems/not-found", "status": 404, "detail": "Voucher not
// found"}. EXPIRED/ALREADY_REDEEMED/CANCELLED are still inferred placeholders — in practice those
// three states are returned as a 200 with voucher_status EXPIRED/REDEEMED/CANCELED in the body
// (see NON_REDEEMABLE_STATUS_BANNERS below) rather than as lookup errors, so these only matter if
// the backend ever also 4xx's a lookup for those states — confirm and adjust the suffix if so.
export enum NeoCashOutVoucherErrorCode {
    NOT_FOUND = 'not-found',
    EXPIRED = 'voucher-expired',
    ALREADY_REDEEMED = 'voucher-already-redeemed',
    CANCELLED = 'voucher-cancelled',
}
